import { api, LightningElement, wire } from "lwc";
import classNames from "classnames";
import debounce from "lodash/debounce";

import { pointsForRect } from "common/geometry";
import { getCurrentInteractionMethod } from "common/interactionMethodObserver";
import {
  AnimationAdapter,
  AnimationAdapterValue,
} from "common/animationAdapter";

const usingKeyboard = () =>
  getCurrentInteractionMethod("control") === "keyboard";

export default class Popover extends LightningElement {
  @api dialogAriaLabel!: string;
  @api buttonAriaLabel!: string;

  get dialogClass() {
    const { openTop, openLeft, fadingOutPreview, fadingOutHover } = this;

    return classNames("popover__dialog", {
      "popover__dialog--top": openTop,
      "popover__dialog--bottom": !openTop,
      "popover__dialog--left": openLeft,
      "popover__dialog--fading-out": fadingOutHover || fadingOutPreview,
      "popover__dialog--animation-disabled": !this.animation.enabled,
    });
  }

  get renderPreviewButton() {
    return (
      this.openedWithPreviewButton ||
      this.focusingWithKeyboard ||
      this.fadingOutPreview
    );
  }

  get renderDialog() {
    return (
      this.openedWithHover ||
      this.openedWithPreviewButton ||
      this.fadingOutHover ||
      this.fadingOutPreview
    );
  }

  private openedWithHover = false;
  private focusingWithKeyboard = false;
  private openedWithPreviewButton = false;
  private fadingOutHover = false;
  private fadingOutPreview = false;
  private openTop = false;
  private openLeft = false;

  @wire(AnimationAdapter)
  private animation: AnimationAdapterValue = null!;

  private get previewButton() {
    return this.template.querySelector(".popover__preview");
  }

  private get focusTrap() {
    return this.template.querySelector("[data-id=focus-trap]");
  }

  handleMouseEnter() {
    this.setOpenedWithHover(true);
  }

  handleMouseLeave() {
    this.setOpenedWithHover(false);
  }

  handleFocusIn() {
    if (!this.openedWithHover) this.setFocusing(true);
  }

  handleFocusOut() {
    this.setFocusing(false);
  }

  handleClickPreview() {
    this.setOpenedWithPreviewButton(!this.openedWithPreviewButton);
  }

  handleAnimationStart(event: AnimationEvent) {
    if (event.animationName === "fadeIn") {
      this.dispatchEvent(new CustomEvent("open"));
    }
  }

  handleAnimationEnd(event: AnimationEvent) {
    if (event.animationName === "fadeOut") {
      this.fadingOutHover = false;
      this.fadingOutPreview = false;
      this.dispatchEvent(new CustomEvent("close"));
    }
  }

  // HACK: This is needed because slotchange only fires once in focusTrap
  // for synthetic shadow. Ideally we just focus first inside focusTrap
  // whenever its slot content changes.
  handleBodyChange() {
    // @ts-ignore
    this.focusTrap?.focusFirst();
  }

  connectedCallback() {
    document.addEventListener("keydown", this.closeOnEscape);
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.closeOnEscape);
  }

  private closeOnEscape = (event: KeyboardEvent) => {
    if (event.key === "Escape" && this.openedWithPreviewButton) {
      if (this.previewButton) this.previewButton.focus();
      this.setOpenedWithPreviewButton(false);
    }
  };

  private setOpenedWithHover = debounce((hovering: boolean) => {
    if (!this.openedWithPreviewButton && this.openedWithHover !== hovering) {
      this.openedWithHover = hovering;
      this.fadingOutHover = !hovering;
      if (hovering) this.setDirection();
    }
  }, 500);

  private setFocusing = debounce((focusing: boolean) => {
    this.focusingWithKeyboard = focusing && usingKeyboard();
    if (this.openedWithPreviewButton && !focusing) {
      this.setOpenedWithPreviewButton(false);
    }
  }, 100);

  private setOpenedWithPreviewButton(previewing: boolean) {
    this.openedWithPreviewButton = previewing;
    this.fadingOutPreview = !previewing;
    if (previewing) this.setDirection();
  }

  private setDirection() {
    const rect = pointsForRect(this.template.host.getBoundingClientRect());
    this.openTop = rect.topLeft.isBottom;
    this.openLeft = rect.topLeft.isRight;
  }
}
